import {
  RoleFeatureDefinition,
  RoleFeatureMatrix,
  RoleFeatureMatrixRole,
} from "../../../access-control/models/role-feature.models";
import {
  createRoleFeatureRole,
  deactivateRoleFeatureRole,
  getAccessControlErrorMessage,
  getRoleFeatureMatrix,
  updateRoleFeatureRole,
} from "../../../access-control/services/access-control.service";
import { themeAtom } from "../../../shared/atoms";
import LoadingWrapper from "../../../shared/components/LoadingWrapper";
import { log, logE, logV } from "../../../shared/services/debug-logger";
import { adminStyles } from "../../styles/admin.styles";
import {
  ArrowPathIcon,
  CheckIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

interface RoleDraft {
  roleKey: string;
  displayTeam: string;
  comment: string;
  permissions: Record<string, boolean>;
}

interface FeatureGroup {
  key: string;
  label: string;
  span: number;
}

const createEmptyPermissions = (features: RoleFeatureDefinition[]) =>
  features.reduce<Record<string, boolean>>((permissions, feature) => {
    permissions[feature.featureKey] = false;
    return permissions;
  }, {});

const createDraftFromRole = (role: RoleFeatureMatrixRole, features: RoleFeatureDefinition[]): RoleDraft => ({
  roleKey: role.roleKey,
  displayTeam: role.displayTeam,
  comment: role.comment,
  permissions: {
    ...createEmptyPermissions(features),
    ...role.permissions,
  },
});

const createBlankDraft = (features: RoleFeatureDefinition[]): RoleDraft => ({
  roleKey: "",
  displayTeam: "",
  comment: "",
  permissions: createEmptyPermissions(features),
});

const buildFeatureGroups = (features: RoleFeatureDefinition[]): FeatureGroup[] => {
  return features.reduce<FeatureGroup[]>((groups, feature) => {
    const previousGroup = groups[groups.length - 1];
    if (previousGroup?.key === feature.featureGroup) {
      previousGroup.span += 1;
      return groups;
    }

    groups.push({
      key: feature.featureGroup,
      label: feature.featureGroup,
      span: 1,
    });
    return groups;
  }, []);
};

const AdminRoleFeatureMatrixPanel = () => {
  const theme = useAtomValue(themeAtom);
  const [matrix, setMatrix] = useState<RoleFeatureMatrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingRoleKey, setSavingRoleKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editingRoleKey, setEditingRoleKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const [createDraft, setCreateDraft] = useState<RoleDraft | null>(null);

  const features = useMemo(() => matrix?.features ?? [], [matrix?.features]);
  const roles = useMemo(() => matrix?.roles ?? [], [matrix?.roles]);
  const featureGroups = useMemo(() => buildFeatureGroups(features), [features]);
  const canMutate = matrix?.source === "snowflake";

  const loadMatrix = useCallback(async () => {
    log("AdminRoleFeatureMatrixPanel - loadMatrix");
    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await getRoleFeatureMatrix();
      log("STATE - applyRoleFeatureMatrix", { count: response.roles.length, source: response.source });
      setMatrix(response);
    } catch (error) {
      logE(new Error("AdminRoleFeatureMatrixPanel - loadMatrix - failed"));
      setErrorMessage(getAccessControlErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  const startEdit = (role: RoleFeatureMatrixRole) => {
    log("AdminRoleFeatureMatrixPanel - startEdit");
    setEditingRoleKey(role.roleKey);
    setDraft(createDraftFromRole(role, features));
    setCreateDraft(null);
    setErrorMessage(null);
  };

  const cancelEdit = () => {
    log("AdminRoleFeatureMatrixPanel - cancelEdit");
    setEditingRoleKey(null);
    setDraft(null);
  };

  const updateDraftField = (field: keyof Omit<RoleDraft, "permissions">, value: string | boolean) => {
    setDraft((current) => (current ? { ...current, [field]: value } : current));
  };

  const updateCreateDraftField = (field: keyof Omit<RoleDraft, "permissions">, value: string | boolean) => {
    setCreateDraft((current) => (current ? { ...current, [field]: value } : current));
  };

  const updateDraftPermission = (featureKey: string, enabled: boolean) => {
    setDraft((current) =>
      current
        ? {
            ...current,
            permissions: { ...current.permissions, [featureKey]: enabled },
          }
        : current,
    );
  };

  const updateCreateDraftPermission = (featureKey: string, enabled: boolean) => {
    setCreateDraft((current) =>
      current
        ? {
            ...current,
            permissions: { ...current.permissions, [featureKey]: enabled },
          }
        : current,
    );
  };

  const saveEdit = async () => {
    if (!draft) {
      return;
    }

    log("AdminRoleFeatureMatrixPanel - saveEdit");
    setSavingRoleKey(draft.roleKey);
    setErrorMessage(null);

    try {
      const response = await updateRoleFeatureRole(draft.roleKey, {
        displayTeam: draft.displayTeam,
        comment: draft.comment,
        permissions: draft.permissions,
      });
      setMatrix(response);
      cancelEdit();
    } catch (error) {
      logE(new Error("AdminRoleFeatureMatrixPanel - saveEdit - failed"));
      setErrorMessage(getAccessControlErrorMessage(error));
    } finally {
      setSavingRoleKey(null);
    }
  };

  const startCreate = () => {
    log("AdminRoleFeatureMatrixPanel - startCreate");
    setCreateDraft(createBlankDraft(features));
    setEditingRoleKey(null);
    setDraft(null);
    setErrorMessage(null);
  };

  const cancelCreate = () => {
    setCreateDraft(null);
  };

  const saveCreate = async () => {
    if (!createDraft) {
      return;
    }

    log("AdminRoleFeatureMatrixPanel - saveCreate");
    setSavingRoleKey("new");
    setErrorMessage(null);

    try {
      const response = await createRoleFeatureRole(createDraft);
      setMatrix(response);
      cancelCreate();
    } catch (error) {
      logE(new Error("AdminRoleFeatureMatrixPanel - saveCreate - failed"));
      setErrorMessage(getAccessControlErrorMessage(error));
    } finally {
      setSavingRoleKey(null);
    }
  };

  const deactivateRole = async (role: RoleFeatureMatrixRole) => {
    const confirmed = window.confirm(`Deactivate ${role.roleKey}?`);
    if (!confirmed) {
      log("AdminRoleFeatureMatrixPanel - deactivateRole - cancelled");
      return;
    }

    log("AdminRoleFeatureMatrixPanel - deactivateRole");
    setSavingRoleKey(role.roleKey);
    setErrorMessage(null);

    try {
      const response = await deactivateRoleFeatureRole(role.roleKey);
      setMatrix(response);
      cancelEdit();
    } catch (error) {
      logE(new Error("AdminRoleFeatureMatrixPanel - deactivateRole - failed"));
      setErrorMessage(getAccessControlErrorMessage(error));
    } finally {
      setSavingRoleKey(null);
    }
  };

  logV("AdminRoleFeatureMatrixPanel - BUILD", { count: roles.length, loading, canMutate });

  if (loading) {
    return <LoadingWrapper isLoading={true} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg" />;
  }

  return (
    <main
      data-test="admin-role-feature-matrix-page"
      className={twMerge("min-h-0 flex-1 overflow-auto", adminStyles.page.pagePadding)}
    >
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className={adminStyles.heading.eyebrow}>Access Control</p>
          <h1 className={twMerge("mt-1", adminStyles.heading.pageTitle, theme.home.tailwind.textDefault)}>
            Role Feature Matrix
          </h1>
          <p className={twMerge("mt-2", adminStyles.heading.body, theme.uwhcAssistant.textOutput)}>
            Manage feature grants for target OpsLink roles.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={loadMatrix}
            className={twMerge(
              "inline-flex h-10 items-center gap-2 border px-3 text-sm font-semibold",
              theme.headerComponent.tailwind.buttonBorder,
            )}
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={startCreate}
            disabled={!canMutate}
            className={twMerge(
              "inline-flex h-10 items-center gap-2 px-3 text-sm font-semibold text-white",
              adminStyles.button.safe,
              adminStyles.button.disabled,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            Add Role
          </button>
        </div>
      </header>

      <div
        className="mb-4 border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
        data-test="admin-role-feature-audience-disclaimer"
      >
        Audience groups are required for login and may change app behavior, but they do not grant feature access and are
        not managed in this matrix.
      </div>

      {errorMessage && (
        <div
          className="mb-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          data-test="admin-role-feature-error"
        >
          {errorMessage}
        </div>
      )}

      {!canMutate && (
        <div
          className="mb-4 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-test="admin-role-feature-fallback-warning"
        >
          Snowflake is unavailable, so the page is using fallback access data and matrix changes are disabled.
        </div>
      )}

      <section className={twMerge(adminStyles.card.surface, "min-h-0 overflow-hidden")} data-test="role-feature-table">
        <div className="max-h-[calc(100vh-260px)] overflow-auto">
          <table className="min-w-max border-separate border-spacing-0 text-left text-xs">
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  className={twMerge(
                    "sticky left-0 top-0 z-40 min-w-[260px] border px-4 py-3 shadow-[inset_-1px_0_0_#E5E7EB]",
                    theme.uwhcAssistant.border,
                    theme.home.tailwind.backgound,
                  )}
                >
                  Role
                </th>
                {featureGroups.map((group) => (
                  <th
                    key={group.key}
                    colSpan={group.span}
                    className={twMerge(
                      "sticky top-0 z-30 h-9 border px-4 py-2 text-center font-semibold uppercase text-green",
                      theme.uwhcAssistant.border,
                      theme.home.tailwind.backgound,
                    )}
                  >
                    {group.label}
                  </th>
                ))}
                <th
                  rowSpan={2}
                  className={twMerge(
                    "sticky right-0 top-0 z-40 min-w-[150px] border px-4 py-3 text-center shadow-[inset_1px_0_0_#E5E7EB]",
                    theme.uwhcAssistant.border,
                    theme.home.tailwind.backgound,
                  )}
                >
                  Actions
                </th>
              </tr>
              <tr>
                {features.map((feature) => (
                  <th
                    key={feature.featureKey}
                    className={twMerge(
                      "sticky top-9 z-30 min-w-[145px] border px-3 py-2 align-bottom",
                      theme.uwhcAssistant.border,
                      theme.home.tailwind.backgound,
                    )}
                    title={feature.description}
                  >
                    <span className="block text-[11px] font-semibold leading-4">{feature.featureLabel}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {createDraft && (
                <DraftRow
                  draft={createDraft}
                  features={features}
                  saving={savingRoleKey === "new"}
                  canMutate={canMutate}
                  isCreate={true}
                  onChange={updateCreateDraftField}
                  onPermissionChange={updateCreateDraftPermission}
                  onSave={saveCreate}
                  onCancel={cancelCreate}
                />
              )}
              {roles.map((role) => {
                const isEditing = editingRoleKey === role.roleKey && !!draft;
                const effectiveDraft = isEditing && draft ? draft : createDraftFromRole(role, features);

                return (
                  <DraftRow
                    key={role.roleKey}
                    role={role}
                    draft={effectiveDraft}
                    features={features}
                    saving={savingRoleKey === role.roleKey}
                    canMutate={canMutate}
                    isEditing={isEditing}
                    onChange={updateDraftField}
                    onPermissionChange={updateDraftPermission}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                    onEdit={() => startEdit(role)}
                    onDeactivate={() => deactivateRole(role)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
};

const DraftRow = ({
  role,
  draft,
  features,
  saving,
  canMutate,
  isCreate = false,
  isEditing = false,
  onChange,
  onPermissionChange,
  onSave,
  onCancel,
  onEdit,
  onDeactivate,
}: {
  role?: RoleFeatureMatrixRole;
  draft: RoleDraft;
  features: RoleFeatureDefinition[];
  saving: boolean;
  canMutate: boolean;
  isCreate?: boolean;
  isEditing?: boolean;
  onChange: (field: keyof Omit<RoleDraft, "permissions">, value: string) => void;
  onPermissionChange: (featureKey: string, enabled: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
  onEdit?: () => void;
  onDeactivate?: () => void;
}) => {
  const theme = useAtomValue(themeAtom);
  const editable = isCreate || isEditing;
  const isActive = role?.isActive ?? true;
  const saveDisabled = !canMutate || saving || !draft.roleKey.trim() || !draft.displayTeam.trim();

  return (
    <tr className={twMerge("border-t", !isActive && "opacity-60", theme.uwhcAssistant.border)}>
      <td
        className={twMerge(
          "sticky left-0 z-10 min-w-[260px] border px-4 py-3 align-top",
          "shadow-[inset_-1px_0_0_#E5E7EB]",
          theme.uwhcAssistant.border,
          theme.home.tailwind.panelBackground,
        )}
      >
        {editable ? (
          <div className="flex flex-col gap-2">
            <MatrixInput
              value={draft.roleKey}
              placeholder="DXP_Opslink_RoleName"
              disabled={!isCreate}
              onChange={(event) => onChange("roleKey", event.target.value)}
            />
            <MatrixInput
              value={draft.displayTeam}
              placeholder="Display team"
              onChange={(event) => onChange("displayTeam", event.target.value)}
            />
            <MatrixInput
              value={draft.comment}
              placeholder="Comment"
              onChange={(event) => onChange("comment", event.target.value)}
            />
          </div>
        ) : (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-semibold">{draft.displayTeam}</p>
              {!isActive && <span className="border border-gray-300 px-2 py-0.5 text-[10px] uppercase">Inactive</span>}
            </div>
            <p className="mt-1 break-all text-[11px] opacity-70">{draft.roleKey}</p>
            {draft.comment && <p className="mt-2 text-[11px] leading-4 opacity-70">{draft.comment}</p>}
          </div>
        )}
      </td>
      {features.map((feature) => (
        <td
          key={feature.featureKey}
          className={twMerge("border px-3 py-3 text-center align-middle", theme.uwhcAssistant.border)}
        >
          <CheckboxCell
            checked={draft.permissions[feature.featureKey] === true}
            disabled={!editable}
            ariaLabel={`${draft.roleKey} ${feature.featureLabel}`}
            onChange={(event) => onPermissionChange(feature.featureKey, event.target.checked)}
          />
        </td>
      ))}
      <td
        className={twMerge(
          "sticky right-0 z-10 border px-3 py-3 align-middle",
          "shadow-[inset_1px_0_0_#E5E7EB]",
          theme.uwhcAssistant.border,
          theme.home.tailwind.panelBackground,
        )}
      >
        <div className="flex items-center justify-center gap-2">
          {editable ? (
            <>
              <IconButton label="Save" disabled={saveDisabled} onClick={onSave}>
                <CheckIcon className="h-4 w-4" />
              </IconButton>
              <IconButton label="Cancel" disabled={saving} onClick={onCancel}>
                <XMarkIcon className="h-4 w-4" />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton label="Edit" disabled={!canMutate || saving || !isActive} onClick={onEdit}>
                <PencilSquareIcon className="h-4 w-4" />
              </IconButton>
              <IconButton label="Deactivate" disabled={!canMutate || saving || !isActive} onClick={onDeactivate}>
                <TrashIcon className="h-4 w-4" />
              </IconButton>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

const MatrixInput = ({
  value,
  placeholder,
  disabled,
  onChange,
}: {
  value: string;
  placeholder: string;
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) => (
  <input
    value={value}
    placeholder={placeholder}
    disabled={disabled}
    onChange={onChange}
    className="h-8 w-full border border-gray-300 bg-white px-2 text-xs outline-none disabled:bg-gray-100"
  />
);

const CheckboxCell = ({
  checked,
  disabled,
  ariaLabel,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) => (
  <label
    className={twMerge(
      "mx-auto flex h-4 w-4 items-center justify-center",
      disabled ? "cursor-not-allowed" : "cursor-pointer",
    )}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={onChange}
      className="sr-only"
    />
    <span
      aria-hidden="true"
      className={twMerge(
        "flex h-4 w-4 items-center justify-center border border-gray-300 bg-white text-white",
        checked && "border-green bg-green",
        disabled && "cursor-not-allowed",
      )}
    >
      {checked && <CheckIcon className="h-3 w-3 stroke-[3]" />}
    </span>
  </label>
);

const IconButton = ({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="inline-flex h-8 w-8 items-center justify-center border border-gray-300 text-green transition-colors hover:bg-[#E9F4F3] disabled:cursor-not-allowed disabled:opacity-40"
  >
    {children}
  </button>
);

export default AdminRoleFeatureMatrixPanel;
